import { Spin } from "antd";
import { useMemo } from "react";
import type { CheckSetupValues } from "./CheckSetup";
import type { PayrollEntryDetailsFragment } from "../../../../lib/graphql/generated";
import type { useFormatMoney } from "../../../../lib/formats/money";
import {
  buildCheckRenderContexts,
  type CheckTemplateFields,
} from "./checkRenderData";
import styles from "./ChecksPreview.module.css";
import cn from "classnames";

type FormatMoneyFn = ReturnType<typeof useFormatMoney>;

interface ChecksPreviewProps {
  setupValues?: CheckSetupValues;
  entry: PayrollEntryDetailsFragment;
  formatMoney: FormatMoneyFn;
}

export function ChecksPreview({
  setupValues,
  entry,
  formatMoney,
}: ChecksPreviewProps) {
  const contexts = useMemo(
    () =>
      buildCheckRenderContexts({
        entry,
        setupValues,
        formatMoney,
      }),
    [entry, formatMoney, setupValues]
  );

  if (!entry || !setupValues) {
    return <Spin />;
  }

  return (
    <div className={styles.previewWrapper}>
      {contexts.map(({ employeeId, fields }, index) => (
        <CheckPreviewCard
          key={employeeId}
          index={index}
          fields={fields}
          bankName={setupValues.bankName}
        />
      ))}
    </div>
  );
}

export function CheckPreviewCard({
  fields,
  index,
  bankName,
}: {
  fields: CheckTemplateFields;
  index: number;
  bankName?: string | null;
}) {
  const ledgerRows = [
    { label: "Devengado" },
    { label: "Extras:" },
    { label: "Bonificación:" },
    { label: "Descuentos:" },
    { label: "Líquido:", value: fields.LIQUIDO_VALUE },
  ];
  const ledgerText = ledgerRows
    .map(({ label, value }) => formatLedgerRow(label, value))
    .join("\n");

  const bankTemplateClass = getBankTemplateClass(bankName);

  return (
    <article
      className={cn(styles.checkCard, bankTemplateClass)}
      aria-label={`Preview for check ${index + 1}`}
    >
      <div className={styles.headerRow}>
        <div>
          <span className={styles.location}>{fields.LOCATION},</span>
          <span>{fields.ISSUE_DATE}</span>
        </div>
        <span className={styles.amountNumeric}>{fields.AMOUNT_NUMERIC}</span>
      </div>

      <div className={styles.payeeRow}>{fields.PAYEE_NAME}</div>
      <div className={styles.amountWords}>{fields.AMOUNT_WORDS}</div>
      <div className={styles.nonNegotiable}>NO NEGOCIABLE</div>

      {fields.PLANILLA_NUMBER && (
        <div className={styles.planillaRow}>
          <span>Planilla Nr.:</span>
          {(fields.PERIOD_START || fields.PERIOD_END) && (
            <span>
              {fields.PERIOD_START} - {fields.PERIOD_END}
            </span>
          )}
        </div>
      )}

      <div className={styles.voucherRow}>
        <span className={styles.voucherIndex}>{fields.PLANILLA_NUMBER}</span>
        <span>{fields.MEMO || fields.PAYEE_NAME}</span>
      </div>

      <div className={styles.ledgerBlockWrapper}>
        {fields.MEMO ? (
          <pre className={styles.ledgerPreBlock}>
            {"\n"}
            {"\n"}
          </pre>
        ) : (
          <pre className={styles.ledgerPreBlock}>{ledgerText}</pre>
        )}
      </div>

      <div className={styles.footerRow}>
        <span>SC</span>
        <span>CAD</span>
      </div>
    </article>
  );
}

const LEDGER_LABEL_WIDTH = 16;
const LEDGER_ROW_WIDTH = 30;

function padLabel(label: string) {
  if (label.length >= LEDGER_LABEL_WIDTH) {
    return label;
  }

  return label.padEnd(LEDGER_LABEL_WIDTH, " ");
}

function formatLedgerRow(label?: string, value?: string) {
  if (!label) return "";

  const labelPadded = padLabel(label);

  if (!value) return labelPadded;

  const normalizedValue = value.trim();
  const spacingLength = Math.max(
    1,
    LEDGER_ROW_WIDTH - (labelPadded.length + normalizedValue.length)
  );
  const spacing = " ".repeat(spacingLength);
  return `${labelPadded}${spacing}${normalizedValue}`;
}

function getBankTemplateClass(bankName?: string | null) {
  if (!bankName) return "";

  // TODO: move to model
  if (bankName.toLowerCase().includes("banco industrial")) {
    return styles.checkCardBancoIndustrial;
  }

  return "";
}
